import type { AnyLocaleType } from './locale-type.js';
import { PSEUDO_LOCALE } from './locales.js';

/**
 * Dates, times, numbers, currency amounts and lists in the person's language (IN-6,
 * `docs/concept-internationalisation.md` §5.6). Every helper takes the locale; none reads the browser's and none
 * hard-codes a tag, so German reads *29. Sept.* and *1.234,5* from the one setting. Relative times are T-0806's
 * (IN-18 to IN-21): `formatRelativeTime` in `web/src/utils/formatRelativeTime.ts` and `<RelativeTime>`, with the
 * style per language from `relativeTimeStyle` (`./locales.ts`).
 *
 * Part of the core: it imports no message formatter, so the backend's CommonJS build may load it. The pseudo
 * locale has no CLDR data; it formats as English, which is what it accents.
 */

let deviceLocales: readonly string[] = [];
let pickedRegion: string | null = null;
const resolved = new Map<string, string>();

/**
 * The device's own locales, best first (`navigator.languages`; the native locale on the phone). The language
 * setting picks the language; the region (en-GB or en-US, de-AT or de-DE) comes from here whenever the device
 * speaks the chosen language, so dates and numbers follow where the reader is. The server and the CLI never
 * set it and stay on the plain language.
 */
export function setDeviceLocales(tags: readonly string[]): void {
  deviceLocales = tags;
  resolved.clear();
}

/**
 * The region the person picked (F-0615), or `null` to follow the device. It wins over the device's while its
 * language is the one being formatted. The web sets it from the setting; the server and the CLI never do.
 */
export function setFormatRegion(tag: string | null): void {
  if (tag === pickedRegion) return;
  pickedRegion = tag;
  resolved.clear();
}

/**
 * The tag `Intl` is given for a locale: its language, plus the picked region when it is that language's, else
 * the device's region when the device speaks it.
 */
export function intlLocale(locale: AnyLocaleType): string {
  const language = locale === PSEUDO_LOCALE ? 'en' : locale;
  let tag = resolved.get(language);
  if (tag === undefined) {
    const picked =
      pickedRegion !== null && pickedRegion.split('-')[0]?.toLowerCase() === language
        ? pickedRegion
        : undefined;
    tag = picked ?? deviceIntlLocale(language);
    resolved.set(language, tag);
  }
  return tag;
}

/** The tag the device alone gives a locale, whatever region is picked: what "Follow this device" would write. */
export function deviceIntlLocale(locale: AnyLocaleType): string {
  const language = locale === PSEUDO_LOCALE ? 'en' : locale;
  return (
    deviceLocales.find((candidate) => {
      const [lang, region] = candidate.split('-');
      return (
        lang?.toLowerCase() === language && region !== undefined && /^[A-Za-z]{2}$/.test(region)
      );
    }) ?? language
  );
}

const cache = new Map<string, unknown>();

/** One `Intl` instance per kind, locale and options: making one is the expensive part of formatting. */
function cached<T>(
  kind: string,
  locale: AnyLocaleType,
  options: object | undefined,
  make: (tag: string) => T,
): T {
  const key = `${kind}|${intlLocale(locale)}|${options ? JSON.stringify(options) : ''}`;
  let found = cache.get(key) as T | undefined;
  if (found === undefined) {
    found = make(intlLocale(locale));
    cache.set(key, found);
  }
  return found;
}

/**
 * A count's ordinal category in `locale`'s rules (`one`, `two`, `few`, `other` in English; only
 * `other` in German), which a message selects its suffix on: "back 2nd", „zum 2. Mal“.
 */
export function ordinalCategory(locale: AnyLocaleType, count: number): Intl.LDMLPluralRule {
  return cached(
    'o',
    locale,
    { type: 'ordinal' },
    (tag) => new Intl.PluralRules(tag, { type: 'ordinal' }),
  ).select(count);
}

/**
 * Two strings in the person's collation, so a list sorted by name sorts the way their language does
 * (`sensitivity`, `numeric` and the rest as `Intl.Collator` takes them).
 */
export function compareText(
  locale: AnyLocaleType,
  a: string,
  b: string,
  options?: Intl.CollatorOptions,
): number {
  return cached('c', locale, options, (tag) => new Intl.Collator(tag, options)).compare(a, b);
}

export function formatNumber(
  locale: AnyLocaleType,
  value: number,
  options?: Intl.NumberFormatOptions,
): string {
  return cached('n', locale, options, (tag) => new Intl.NumberFormat(tag, options)).format(value);
}

/** A money amount in the person's locale, in the currency the price states: the language never converts money. */
export function formatCurrency(
  locale: AnyLocaleType,
  value: number,
  currency: string,
  options?: Intl.NumberFormatOptions,
): string {
  return formatNumber(locale, value, { ...options, style: 'currency', currency });
}

export function formatDate(
  locale: AnyLocaleType,
  value: Date | number | string,
  options?: Intl.DateTimeFormatOptions,
): string {
  const date = value instanceof Date ? value : new Date(value);
  return cached('d', locale, options, (tag) => new Intl.DateTimeFormat(tag, options)).format(date);
}

/** The same date as its parts, for a label that keeps only some of them (a bare hour). */
export function formatDateParts(
  locale: AnyLocaleType,
  value: Date | number | string,
  options?: Intl.DateTimeFormatOptions,
): Intl.DateTimeFormatPart[] {
  const date = value instanceof Date ? value : new Date(value);
  return cached('d', locale, options, (tag) => new Intl.DateTimeFormat(tag, options)).formatToParts(
    date,
  );
}

export function formatList(
  locale: AnyLocaleType,
  items: readonly string[],
  options: Intl.ListFormatOptions = { style: 'long', type: 'conjunction' },
): string {
  return cached('l', locale, options, (tag) => new Intl.ListFormat(tag, options)).format(items);
}

/**
 * One choice of the Formats setting (F-0615): the region's name in the page's language, and a date written the
 * region's way, so a person sees what they pick. `tag` is a `FORMAT_REGIONS` entry.
 */
export function formatRegionOption(
  locale: AnyLocaleType,
  tag: string,
  value: Date | number,
): { name: string; example: string } {
  const region = tag.split('-')[1] ?? tag;
  const names = cached(
    'r',
    locale,
    undefined,
    (page) => new Intl.DisplayNames(page, { type: 'region' }),
  );
  const key = `x|${tag}`;
  let example = cache.get(key) as Intl.DateTimeFormat | undefined;
  if (example === undefined) {
    example = new Intl.DateTimeFormat(tag, REGION_EXAMPLE);
    cache.set(key, example);
  }
  return { name: names.of(region) ?? tag, example: example.format(value) };
}

const REGION_EXAMPLE: Intl.DateTimeFormatOptions = {
  day: 'numeric',
  month: 'long',
  year: 'numeric',
  timeZone: 'UTC',
};
